/**
 * Where an edge should meet each shape.
 *
 * A node's box is not its outline.  Every shape is drawn inside a box that also carries the
 * content padding, and the pointed shapes are inset much further than that at the height an edge
 * arrives: a triangle's left face at mid-height is a quarter of the way across its box.  An edge
 * stopped at the box floats in empty space beside them.
 *
 * The outlines below are the same coordinates the shapes are drawn from in `nodeTypes/shapes.tsx`,
 * so the attachment points are computed from the real geometry rather than eyeballed.  The
 * previous approach hard-coded a percentage per side, which was close but never exact -- a
 * triangle was pinned at 22% where its face is actually at 25.5%.
 *
 * This is the single source of truth for both the react-flow handle positions and the endpoints of
 * the routes the layout engine produces, which is the only way the two can agree: the engine knows
 * nothing about the shape drawn inside a box it was handed.  Both consumers measure from the
 * CONTENT box -- see `handleOffsetsFor`, which has to say so in `calc()` to match.
 *
 * No React, no stylesheet, no layout engine, so both sides can import it freely.
 */
import type { HandlePosition } from "./layout";

/**
 * A side of a shape.
 *
 * An alias of `HandlePosition`: this was a fourth byte-identical copy of the same union, and the
 * handle a link attaches to and the side of the outline it is seated against are by definition the
 * same thing.  Kept as a name because it is emitted into the published `.d.ts`.
 */
export type ShapeSide = HandlePosition;

/** Padding between a node's box and the shape inside it. Mirrors `.FlowChart-node` padding. */
export const NODE_CONTENT_PADDING = 4;

/**
 * Stroke applied to the shapes, in viewBox units. Mirrors `.node-shape *` in FlowChart.scss.
 *
 * The polygons below are path centrelines, so the visible outline is half a stroke further out.
 * On a slanted face that half-stroke reaches further along the measuring axis than it does
 * perpendicular to the face, by a factor of the face's slope -- which is why simply taking the
 * centreline left a visible gap on the triangles.
 */
const SHAPE_STROKE = 6;

type Outline = {
    /** The `viewBox` the shape is drawn in; it is stretched to fill the box. */
    viewBox: { width: number; height: number };
    /** Closed polygons approximating the drawn outline, in viewBox coordinates. */
    polygons: [number, number][][];
};

/**
 * Outlines, in the shapes' own drawing coordinates.
 *
 * Curves are approximated by their bounding polygon, which is exact at the mid-lines that matter
 * here: a rounded rectangle's rounding only pulls in its corners, never the middle of a side.
 */
const OUTLINES: Record<string, Outline> = {
    rectangle: {
        viewBox: { width: 100, height: 60 },
        polygons: [[[1, 1], [99, 1], [99, 59], [1, 59]]],
    },
    "rounded-rectangle": {
        viewBox: { width: 100, height: 60 },
        polygons: [[[1, 1], [99, 1], [99, 59], [1, 59]]],
    },
    chevron: {
        viewBox: { width: 100, height: 60 },
        polygons: [[[1, 1], [20, 30], [1, 59], [80, 59], [99, 30], [80, 1]]],
    },
    diamond: {
        viewBox: { width: 100, height: 100 },
        polygons: [[[1, 50], [50, 1], [99, 50], [50, 99]]],
    },
    triangle: {
        viewBox: { width: 100, height: 80 },
        polygons: [[[1, 79], [50, 1], [99, 79]]],
    },
    "multi-triangle": {
        viewBox: { width: 140, height: 90 },
        polygons: [
            [[50, 1], [1, 80], [100, 80]],
            [[90, 1], [40, 80], [139, 80]],
            [[70, 10], [20, 89], [120, 89]],
        ],
    },
};

/** `default` renders the rectangle, so it attaches like one. */
OUTLINES["default"] = OUTLINES["rectangle"];

/**
 * How far inside its viewBox an outline sits on one side, at a given point along that side.
 *
 * `along` is a fraction of the shape: 0 is the top (or left) end of the side, 1 the bottom (or
 * right), 0.5 the middle.  It matters because a sloping face is at a different depth at every
 * height -- a triangle's left face is nearly at its box edge at the base and half way across at
 * the apex.  Measuring only at the middle put every link arriving at a triangle in the same
 * place: the ones above the middle stopped short in empty space, the ones below overshot inside
 * the shape.
 *
 * Walks every polygon edge, keeps those that span the sample line, and takes the outermost
 * crossing.  For a multi-part shape that is the union's silhouette, which is what a link meets.
 * Returns `undefined` where the line misses the outline entirely -- above a triangle's apex,
 * say.
 */
const crossingInset = (outline: Outline, side: ShapeSide, along = 0.5): number | undefined => {
    const { width, height } = outline.viewBox;
    const horizontal = side === "left" || side === "right";
    const sample = horizontal ? along * height : along * width;
    const midX = horizontal ? width / 2 : sample;
    const midY = horizontal ? sample : height / 2;
    const crossings: { at: number; reach: number }[] = [];

    for (const polygon of outline.polygons) {
        for (let i = 0; i < polygon.length; i++) {
            const [x1, y1] = polygon[i];
            const [x2, y2] = polygon[(i + 1) % polygon.length];

            const length = Math.hypot(x2 - x1, y2 - y1);
            if (length === 0) continue;

            if (horizontal) {
                // Where this edge crosses the horizontal mid-line.
                if (y1 === y2 || (y1 - midY) * (y2 - midY) > 0) continue;
                const at = x1 + ((midY - y1) / (y2 - y1)) * (x2 - x1);
                // How far the stroke's outer face is from the centreline, along this axis.
                const slope = Math.abs(y2 - y1) / length;
                crossings.push({ at, reach: (SHAPE_STROKE / 2) / slope });
            } else {
                if (x1 === x2 || (x1 - midX) * (x2 - midX) > 0) continue;
                const at = y1 + ((midX - x1) / (x2 - x1)) * (y2 - y1);
                const slope = Math.abs(x2 - x1) / length;
                crossings.push({ at, reach: (SHAPE_STROKE / 2) / slope });
            }
        }
    }

    if (!crossings.length) {
        return undefined;
    }

    // The outermost visible edge on that side. Deliberately allowed to go NEGATIVE: the stroke
    // spreads either side of the path, so a rectangle drawn at x=1 with a 6px stroke reaches
    // x=-2 -- outside its own viewBox, and so outside the content box the shape is drawn in.
    // Clamping here would charge the full content padding on top of an outline that already
    // reaches the node's border, which pushes the arrowhead into the node.
    switch (side) {
        case "left": return Math.min(...crossings.map(c => c.at - c.reach));
        case "top": return Math.min(...crossings.map(c => c.at - c.reach));
        case "right": return width - Math.max(...crossings.map(c => c.at + c.reach));
        case "bottom": return height - Math.max(...crossings.map(c => c.at + c.reach));
    }
};

const SIDES: ShapeSide[] = ["top", "left", "bottom", "right"];

/**
 * Fraction of the shape's box that the outline sits inside it, per side.
 *
 * Computed once from the outlines above, so it cannot drift from the drawn shapes.
 */
const SHAPE_INSETS: Record<string, Record<ShapeSide, number>> = Object.fromEntries(
    Object.entries(OUTLINES).map(([variant, outline]) => [
        variant,
        Object.fromEntries(SIDES.map(side => [
            side,
            (crossingInset(outline, side) ?? 0) /
                (side === "left" || side === "right" ? outline.viewBox.width : outline.viewBox.height),
        ])) as Record<ShapeSide, number>,
    ])
);

/**
 * Per-side inline styles that place a handle on the outline, as the node factory wants them.
 *
 * The offset is expressed as `calc()` rather than a bare percentage so that it is the SAME
 * quantity `shapeInset` computes for the routed endpoints: the shape is drawn inside the content
 * box, so the depth is `padding + fraction * (size - 2 * padding)`, not `fraction * size`.  A
 * plain percentage differed from the routed endpoint by `4 - 8f` px -- about 2.7px on a chevron
 * -- which is exactly the drift this module exists to prevent.  `calc` keeps it exact at any
 * node size, which a number computed here could not be.
 */
export const handleOffsetsFor = (variant: string) =>
    Object.fromEntries(
        SIDES
            // Only worth an inline style where the outline is meaningfully inside the box.
            .filter(side => (SHAPE_INSETS[variant]?.[side] ?? 0) > 0.02)
            .map((side) => {
                const fraction = SHAPE_INSETS[variant][side];
                const offset = NODE_CONTENT_PADDING * (1 - 2 * fraction);
                return [side, {
                    [side]: `calc(${(fraction * 100).toFixed(1)}% + ${offset.toFixed(2)}px)`,
                }];
            })
    );

/** Every shape with a defined outline. */
export const shapeVariants = () => Object.keys(OUTLINES);

/** How far in from the end of a side to step while looking for a sample that meets the outline. */
const INSET_SEARCH_STEP = 0.005;

/**
 * The inset at `along`, or at the nearest point further IN that the sample line actually meets.
 *
 * Near the end of a side the line misses a pointed outline entirely -- there is no shape left to
 * cross up in a triangle's corner -- and falling back to the MIDDLE of the side, which is what
 * this used to do, makes the function jump.  On a 150x100 triangle the left inset runs 67px at
 * `along` 0.05 down to 3px at 0.95 and then snaps back to the mid-side 35px for anything past
 * ~0.985, so an endpoint landing in that band seats its arrowhead 30px inside the shape.  That
 * band is reachable: `layout.ts` computes `along` unclamped against a box already inset by
 * `NODE_CONTENT_PADDING`, so a routed endpoint at a corner lands in it.
 *
 * Stepping inward keeps the value continuous, because where the outline runs out the nearest
 * sample that hits IS the extreme.  The mid-side fallback is kept for a shape no sample meets.
 */
const insetNear = (outline: Outline, side: ShapeSide, along: number): number => {
    const clamped = Math.min(Math.max(along, 0), 1);
    const step = clamped > 0.5 ? -INSET_SEARCH_STEP : INSET_SEARCH_STEP;

    for (let at = clamped; at >= 0 && at <= 1; at += step) {
        const hit = crossingInset(outline, side, at);
        if (hit !== undefined) {
            return hit;
        }
    }

    return crossingInset(outline, side, 0.5) ?? 0;
};

/**
 * How far inside the node's box the outline sits on one side, in pixels.
 *
 * The shape is drawn inside the content box, so the padding is added on top of the outline's own
 * inset.  An unknown shape -- or a group, which has no shape -- gets the padding alone, which is
 * what stops an arrowhead sitting clear of a plain rectangle.
 */
export const shapeInset = (
    variant: string | undefined,
    side: ShapeSide,
    width: number,
    height: number,
    /** Where along the side the link meets it, as a fraction of the shape. Defaults to the middle. */
    along = 0.5
) => {
    const outline = OUTLINES[variant ?? ""];
    if (!outline) {
        return NODE_CONTENT_PADDING;
    }

    const horizontal = side === "left" || side === "right";
    const inset = insetNear(outline, side, along);
    const viewSpan = horizontal ? outline.viewBox.width : outline.viewBox.height;
    const span = (horizontal ? width : height) - 2 * NODE_CONTENT_PADDING;

    // A negative fraction eats into the padding, which is exactly right for a shape whose stroke
    // already reaches the node's border. Never past the border itself, though.
    return Math.max(0, NODE_CONTENT_PADDING + (inset / viewSpan) * Math.max(0, span));
};
